import { useEffect, useRef, useState } from "react";
import { Box, Flex } from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useGateValue } from "@statsig/react-bindings";
import { cn } from "@/lib/utils";
import TrackCard from "@/components/ui/TrackCard";
import { useTracksInfiniteList } from "@/lib/lutely-api/hooks";
import { getKeyQueryPitches } from "./FilterSection/shared/keySignatures";
import { TracksControllerTracksListSortBy } from "@/lib/lutely-api/gen/dtos/tracksControllerTracksListSortBy";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import FilterBar from "./FilterBar";
import TopFiltersControls from "./TopFiltersControls";
import MobileFiltersDrawer from "./mobile-filters/MobileFiltersDrawer";
import CreatorsSection from "./CreatorsSection";
import { MarketplaceTabs } from "./MarketplaceTabs";
import { analytics, EventEnum } from "@/lib/analytics";
import {
  MarketplaceSearch,
  MarketplaceUpdates,
  MarketplaceFilterKey,
} from "./types";
import InfiniteScrollSentinel from "../ui/InfiniteScrollSentinel";
import {
  mapSortToApiParams,
  deClusterArtists,
  SORT_OPTIONS,
} from "@/lib/marketplace-sort";

export type TrackKind = "SONG" | "BEAT" | "SOUNDKIT";

type Props = {
  kind: TrackKind;
};

export default function TracksMarketplacePage({ kind }: Props) {
  const marketplacePath =
    kind === "SONG"
      ? "/marketplace/songs"
      : kind === "BEAT"
        ? "/marketplace/beats"
        : "/marketplace/soundkits";
  const search = useSearch({ strict: false }) as MarketplaceSearch;
  const navigate = useNavigate();

  const songsMarketplaceEnabled = useGateValue("songs_marketplace");

  // Guard against deep links / history landing on the gated Songs page.
  useEffect(() => {
    if (kind === "SONG" && !songsMarketplaceEnabled) {
      navigate({ to: "/marketplace/albums", replace: true });
    }
  }, [kind, songsMarketplaceEnabled, navigate]);

  const isDesktopLayout = useMediaQuery("(min-width: 1024px)");
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isFiltersStuck, setIsFiltersStuck] = useState(false);
  const stickySentinelRef = useRef<HTMLDivElement>(null);

  // ---- filter handlers ----
  const onSetFilter = (updates: MarketplaceUpdates) => {
    analytics.track(EventEnum.MARKETPLACE_FILTER_SELECTED, {
      updatedFilters: updates,
    });

    navigate({
      to: marketplacePath,
      search: (old) => ({
        ...old,
        ...updates,
      }),
      resetScroll: false,
    });
  };

  const onRemoveFilter = (param: MarketplaceFilterKey) => {
    navigate({
      to: marketplacePath,
      search: (old) => ({
        ...old,
        [param]: undefined,
      }),
      resetScroll: false,
    });
  };

  // ---- derive query params ----
  const { bpm, genre, mood, key, query, types, vocals, year, sort } = search;
  const effectiveSort = sort;
  const effectiveSearch = search;
  const hasActiveFilters = Boolean(
    query || bpm || genre || mood || key || types || vocals || year,
  );

  let bpmStart: number | undefined;
  let bpmEnd: number | undefined;
  let genres: string[] | undefined;
  let moods: string[] | undefined;
  let keySignatures: string[] | undefined;

  if (bpm) {
    const [a, b] = bpm.split("-");
    bpmStart = a ? parseInt(a, 10) : undefined;
    bpmEnd = b ? parseInt(b, 10) : undefined;
  }

  if (genre) genres = genre.split(", ");
  if (mood) moods = mood.split(", ");
  if (key) keySignatures = getKeyQueryPitches(key) ?? [key];

  const sortApiParams = mapSortToApiParams(effectiveSort);

  const tracksQuery = useTracksInfiniteList({
    type: kind,
    pageSize: 20,
    published: true,
    bpmStart,
    bpmEnd,
    genres,
    moods,
    keySignatures,
    search: query,
    sortBy: sortApiParams.sortBy as TracksControllerTracksListSortBy,
    sortDirection: sortApiParams.sortDirection,
    seed: sortApiParams.seed,
  });

  const rawTracks = tracksQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const tracks =
    !effectiveSort || effectiveSort === "discover"
      ? deClusterArtists(rawTracks)
      : rawTracks;

  useEffect(() => {
    if (!isDesktopLayout) return;
    const sentinel = stickySentinelRef.current;
    const scrollContainer = document.querySelector(".main-scroll-container");
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFiltersStuck(!entry.isIntersecting);
      },
      {
        threshold: 0,
        root: scrollContainer,
        rootMargin: "-1px 0px 0px 0px",
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isDesktopLayout]);

  const pageTitle =
    kind === "SONG" ? "Songs" : kind === "BEAT" ? "Beats" : "Sound Kits";

  return (
    <Box className={cn("w-full", !isDesktopLayout && "pt-[16px] pb-[230px]")}>
      <h1
        className="mb-6 font-figtree text-[28px] font-bold leading-[110%] tracking-[-0.48px] text-[#050C2B] md:text-[48px]"
        style={{ fontFamily: "var(--Primary, Figtree)" }}
      >
        {pageTitle}
      </h1>

      {!isDesktopLayout && (
        <Flex className="w-full flex-col gap-[12px] shrink-0 mb-4">
          <MarketplaceTabs
            onFiltersClick={() => setIsMobileFiltersOpen(true)}
          />
          {hasActiveFilters && (
            <Flex className="-mx-4 overflow-x-auto pb-1 px-4">
              <FilterBar
                searchParameters={search}
                onRemoveFilter={onRemoveFilter}
                className="mb-0 min-w-max flex-nowrap"
              />
            </Flex>
          )}
        </Flex>
      )}

      {isDesktopLayout && (
        <>
          <div ref={stickySentinelRef} className="h-px w-full" aria-hidden />
          <Box
            className={cn(
              "sticky top-0 z-30 bg-gray-100",
              isFiltersStuck &&
                "border-t border-[rgba(12,19,38,0.08)] shadow-[0_1px_0_rgba(12,19,38,0.08)]",
            )}
          >
            <Box
              className={cn(
                hasActiveFilters ? "pb-2" : isFiltersStuck ? "" : "pb-10",
                isFiltersStuck && hasActiveFilters && "pt-4",
              )}
            >
              <TopFiltersControls
                searchParameters={effectiveSearch}
                onSetFilter={onSetFilter}
                onRemoveFilter={onRemoveFilter}
                sortOptions={SORT_OPTIONS}
              />
            </Box>
            {hasActiveFilters && (
              <Box className={cn("mb-4", isFiltersStuck && "pb-4")}>
                <FilterBar
                  searchParameters={search}
                  onRemoveFilter={onRemoveFilter}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <CreatorsSection
        hideArrows={false}
        kind={kind}
        searchParameters={search}
      />

      <Flex direction="column" className="pt-[40px]">
        <Box className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-[24px]">
          {tracks.map((t) => (
            <Box key={t.trackId} className="min-w-0">
              <TrackCard track={t} className="w-full" />
            </Box>
          ))}
        </Box>

        <InfiniteScrollSentinel
          hasNextPage={tracksQuery.hasNextPage}
          isFetchingNextPage={tracksQuery.isFetchingNextPage}
          fetchNextPage={tracksQuery.fetchNextPage}
        />
        {!tracksQuery.hasNextPage && tracks.length > 0 && (
          <Flex className="justify-center text-md text-gray-500 mt-8">
            End of list
          </Flex>
        )}
      </Flex>

      {!isDesktopLayout && (
        <MobileFiltersDrawer
          open={isMobileFiltersOpen}
          onOpenChange={setIsMobileFiltersOpen}
          searchParameters={effectiveSearch}
          onSetFilter={onSetFilter}
          onRemoveFilter={onRemoveFilter}
          sortOptions={SORT_OPTIONS}
        />
      )}
    </Box>
  );
}
